import {
  ResultadoDeAliasSchema,
  ResultadoDeEliminarEmpresasSchema,
  ResultadoDeGuardarEmpresaSchema,
  type AliasDeEmpresa,
  type DatosDeEmpresa,
  type EliminarEmpresas,
  ListaDeEmpresasSchema,
  ResultadoDeUnirEmpresasSchema,
  ResultadoDeUnirVariasEmpresasSchema,
  type UnirEmpresas,
  type UnirVariasEmpresas,
} from '@cnc/contracts';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pedir} from './cliente';
import {refrescarDatos} from './refrescar';

export function useEmpresas() {
  return useQuery({queryKey: ['empresas'], queryFn: () => pedir('empresas', ListaDeEmpresasSchema)});
}

// Crear una empresa con su ficha, o editarla con la version que se leyo (editores y administradores).
export function useGuardarEmpresa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({id, datos}: {id: number | null; datos: DatosDeEmpresa & {version?: number}}) =>
      pedir(id ? `empresas/${id}` : 'empresas', ResultadoDeGuardarEmpresaSchema, {
        method: id ? 'PUT' : 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(datos),
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['empresas']});
      await refrescarDatos(cliente);
    },
  });
}

// Unir una empresa duplicada con la correcta (solo administradores).
export function useUnirEmpresas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: UnirEmpresas) =>
      pedir('empresas/unir', ResultadoDeUnirEmpresasSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['empresas']});
      await refrescarDatos(cliente);
    },
  });
}

// Otros nombres de una empresa (NIM = Nimbus); la crea si no existe (solo administradores).
export function useAliasDeEmpresa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: AliasDeEmpresa) =>
      pedir('empresas/alias', ResultadoDeAliasSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['empresas']});
      await refrescarDatos(cliente);
    },
  });
}

// Eliminar empresas (solo administradores): revisar dice que queda sin empresa; eliminar las borra.
export function useEliminarEmpresas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: EliminarEmpresas) =>
      pedir('empresas/eliminar', ResultadoDeEliminarEmpresasSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async r => {
      if (!r.eliminado) return;
      await cliente.invalidateQueries({queryKey: ['empresas']});
      await refrescarDatos(cliente);
    },
  });
}

// Unir varias marcadas con una sola, en un paso (solo administradores).
export function useUnirVariasEmpresas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: UnirVariasEmpresas) =>
      pedir('empresas/unir-varias', ResultadoDeUnirVariasEmpresasSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['empresas']});
      await refrescarDatos(cliente);
    },
  });
}
